import { action, computed, makeObservable, observable } from 'mobx';
import { CallService, MeetCapabilities } from '../services/callService';

/**
 * Where Meet is offered and what this user may do in it, as the server decides it.
 *
 * Three separate things used to answer this, none of them the server: `features.loanChatMeet`,
 * compiled into environment.ts and true in every build; `userStore.isBackOfficeUser`; and
 * `rocChatGlobalDataStore.isInternalChat`. The mobile app had its own, differently worded, copy
 * of the same rules. One request now answers all of it, which is the only way the two apps can
 * be relied on to agree — and the only way a role change or a kill switch reaches a desktop that
 * was installed months ago.
 *
 * A store rather than a hook's state because the answer is wanted in three places that do not
 * share a parent: the toolbar button in feature-app-core, the "create" menu and Huddle in
 * roc-chat. One load, one answer.
 */
export class MeetCapabilitiesStore {
  capabilities: MeetCapabilities = null;

  /**
   * The in-flight request, so that three components mounting at once make one call.
   *
   * Kept after it resolves as well, which makes load() a once-per-window operation. Capabilities
   * follow the signed-in user and the window is torn down on logout, so there is nothing to
   * re-read; a window that outlived a role change would be refreshed by its own reload.
   */
  private loaded: Promise<void> = null;

  private callService: CallService;

  /*
   * Note for anyone moving this store: it must stay off _BaseStore.
   *
   * service.ts imports ../stores/_baseStore directly, before the ../stores barrel, so anything
   * _baseStore pulls in is evaluated while service.ts is still running. callService does
   * `class CallService extends Service` at module scope, so landing in that early branch kills
   * the app on load with "Cannot access 'Service' before initialization". The other stores that
   * hold a service -- userStore, loginStore, globalStore -- are safe because they only reach for
   * it at runtime, and because they arrive through the barrel rather than that direct import.
   */
  constructor() {
    this.callService = new CallService();
    makeObservable(this, {
      capabilities: observable,
      meetAvailable: computed,
      canMakeCalls: computed,
      canCreateRoom: computed,
      canSearchDirectory: computed,
      incomingCallsRefused: computed,
      setCapabilities: action,
    });
  }

  setCapabilities(capabilities: MeetCapabilities) {
    this.capabilities = capabilities;
  }

  /**
   * Whether this app offers Meet at all.
   *
   * Reads the desktop flag because this store only ever runs in roc — the Electron app and the
   * same bundle served at /chat-ui, both of which are the desktop client as far as the server is
   * concerned. The mobile answer travels in the same response and is not ours to read.
   */
  get meetAvailable(): boolean {
    return this.capabilities?.desktopMeetAvailable === true;
  }

  /**
   * Placing a call at all — Huddle included, whether it dials one person or opens a room.
   *
   * Huddle was briefly gated on canCreateRoom, which is the wrong question: ringing someone
   * already in the conversation creates no room, and borrowers and lenders are callees rather
   * than callers for reasons that have nothing to do with rooms.
   */
  get canMakeCalls(): boolean {
    return this.capabilities?.canMakeCalls === true;
  }

  /** Opening an ad-hoc room from the create menu. Narrower than canMakeCalls. */
  get canCreateRoom(): boolean {
    return this.capabilities?.canCreateRoom === true;
  }

  get canSearchDirectory(): boolean {
    return this.capabilities?.canSearchDirectory === true;
  }

  /**
   * Whether the server has told us this user may *not* be rung.
   *
   * Phrased as a refusal, and defaulting to false, because the incoming path has to fail in the
   * opposite direction to every other getter here. Those start closed: showing a control to
   * someone who should not have it is worse than showing it a moment late. This one starts open:
   * silently not ringing someone's desk because a capabilities request had not come back, or had
   * failed, would be a far worse bug than ringing a role a future rule would have spared — and
   * unlike the outgoing controls, the server already enforces this one at the invite fan-out, so
   * the client is the second line here rather than the only one.
   *
   * True for no role today, which is exactly why it must not be allowed to go wrong quietly.
   */
  get incomingCallsRefused(): boolean {
    return this.capabilities ? this.capabilities.canReceiveCalls !== true : false;
  }

  /**
   * Fetch once per window, and only once it can succeed.
   *
   * Callers pass an `enabled` flag to useMeetCapabilities rather than calling this on mount: in
   * the Meet window the component mounts before loadSavedPortalConfigurationHost and
   * checkAuthentiation have run, so an unconditional call here goes out with no session and
   * comes back 401.
   *
   * Everything starts closed and opens on the response. The alternative shows a borrower a
   * directory search for as long as the request takes and then takes it away, which is worse
   * than a control that appears a moment late; and a user whose capabilities never load keeps
   * the two things everyone has — being called, and joining a room by code.
   */
  load(): Promise<void> {
    if (!this.loaded) {
      this.loaded = this.callService
        .capabilities()
        .then(response => {
          this.setCapabilities(response?.data?.data);
        })
        .catch(e => {
          /*
           * Clear the guard so the next attempt actually tries again.
           *
           * Holding on to the settled promise cached the *failure*: the Meet window asked before
           * its session existed, got a 401, and then short-circuited every later call for the
           * life of the window -- so the directory and New room stayed hidden even once the user
           * was plainly signed in. Callers gate on something that changes when there is a session
           * to use, so this does not turn into a request per render.
           */
          this.loaded = null;
          console.error('Could not load Meet capabilities', e);
        });
    }
    return this.loaded;
  }
}
